import { useEffect, useState } from "react";
import {
  BotIcon,
  CircleAlertIcon,
  CoinsIcon,
  TrendingUpIcon,
  TriangleAlertIcon,
  XIcon,
} from "lucide-react";

import { NotificationMarkReadButton } from "./NotificationMarkReadButton";
import { Column, Row, Text } from "@/components";
import { StafferPetIncentiveToken } from "@/components/staffer-pets/StafferPetIncentiveToken";
import { StafferPetProfileSprite } from "@/components/staffer-pets/StafferPetProfileSprite";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useGetPets } from "@/hooks/queries/useGetPets";
import { useUpdateSetNotifications } from "@/hooks/updateSets/useUpdateSetNotifications";
import {
  STAFFER_PET_REWARD_DETAILS,
  STAFFER_PET_SEARCH_SUBMIT_REWARD,
} from "@/hooks/useStafferPets";
import { CORNER_POPUP_MIN_HEIGHT_CLASS, MS_PER_SECOND } from "@/lib/constants";
import { dates } from "@/lib/dates";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import {
  MIXPANEL_NOTIFICATION_SURFACES,
  MIXPANEL_SCREENS,
} from "@/lib/mixpanel/types";
import { getNotificationLink } from "@/lib/notificationLinks";
import { cn } from "@/lib/utils";
import {
  NotificationActiveCandidateFinishedSteviePayload,
  NotificationIncentiveRedeemedPayload,
  NotificationResponse,
  NotificationTeamWideSubmitPayload,
  NotificationType,
  Pet,
} from "@/types";

// Two pets is all the card's height holds, so a third and fourth grow the rail
// sideways into a second column instead of stacking further.
const MAX_SUBMITTER_PETS = 4;
const SUBMITTER_PETS_PER_COLUMN = 2;
const SUBMITTER_PET_SIZE_PX = 60;

const NUDGES_PER_ESCALATION = 3;
const NUDGE_BEFORE_ESCALATION = 2;

// The incentive celebration (bounce + sparkles) plays through in ~2s, so it
// replays on a loop while the card is up rather than only at mount, where the
// card's own slide-in can swallow it.
const CELEBRATION_REPLAY_INTERVAL_MS = 3_500;

// Body type shrinks a step at a time as the content gets longer, so a two-line
// notification keeps its normal size and a submit roundup naming three staffers
// still fits without the card ballooning. Lengths are what each size holds in
// the ~272px the body gets when the pet rail is showing, at two to three lines.
const CONTENT_SIZE_STEPS = [
  { maxLength: 80, className: "text-sm leading-5" },
  { maxLength: 180, className: "text-xs leading-4" },
  { maxLength: Infinity, className: "text-[11px] leading-[14px]" },
];

export const NotificationPopupCard = ({
  isExiting = false,
  notification,
  onDismiss,
  onDismissAll,
}: {
  isExiting?: boolean;
  notification: NotificationResponse;
  onDismiss: (notificationId: number) => void;
  // Only passed when this is the top card of a pile worth clearing at once.
  onDismissAll?: () => void;
}) => {
  const { mutate: updateNotifications } = useUpdateSetNotifications();
  const { data: pets } = useGetPets();
  const notificationLink = getNotificationLink(
    notification.type,
    notification.payload,
  );
  const isTeamWideSubmit =
    notification.type === NotificationType.TEAM_WIDE_SUBMIT;
  // The FE owns this type's copy — the stored title/content are a fallback.
  const incentive =
    notification.type === NotificationType.INCENTIVE_REDEEMED
      ? (notification.payload as NotificationIncentiveRedeemedPayload)
      : null;
  const title = incentive ? "Incentive redeemed!" : notification.title;
  const content = incentive
    ? `${incentive.redeemerFirstName} cashed in an incentive token for ${incentive.description}!`
    : notification.content;
  const isStevieCall =
    notification.type === NotificationType.ACTIVE_CANDIDATE_FINISHED_STEVIE;
  const stevie = isStevieCall
    ? (notification.payload as NotificationActiveCandidateFinishedSteviePayload)
    : null;
  const isElevatedStevieCall =
    stevie?.fromScore != null && stevie.toScore > stevie.fromScore;
  // notifiedCount counts appearances and the notification itself was the first, so
  // the nudge number always sits one behind it
  const nudgeNumber = notification.notifiedCount - 1;
  const isEscalatingNextNudge =
    nudgeNumber > 0 &&
    nudgeNumber % NUDGES_PER_ESCALATION === NUDGE_BEFORE_ESCALATION;
  const isEscalated =
    nudgeNumber > 0 && nudgeNumber % NUDGES_PER_ESCALATION === 0;

  // Remounting the celebration on each tick restarts its CSS animations.
  const [celebrationSequence, setCelebrationSequence] = useState(0);
  const isIncentiveRedeemed = incentive !== null;
  useEffect(() => {
    if (!isIncentiveRedeemed) return;

    const replayInterval = window.setInterval(() => {
      setCelebrationSequence((sequence) => sequence + 1);
    }, CELEBRATION_REPLAY_INTERVAL_MS);
    return () => {
      window.clearInterval(replayInterval);
    };
  }, [isIncentiveRedeemed]);

  const contentSizeClassName = () =>
    CONTENT_SIZE_STEPS.find(({ maxLength }) => content.length <= maxLength)
      ?.className;

  // How long the notification sat before the user acted on this popup.
  const secondsSinceCreated = () =>
    Math.round(
      (Date.now() - new Date(notification.createdAt).getTime()) / MS_PER_SECOND,
    );

  const handleMarkAsRead = () => {
    updateNotifications({
      notificationIds: [notification.id],
      markAsRead: true,
    });
  };

  const handleCardClick = () => {
    if (!notificationLink) return;
    mixpanelUtils.trackRowClick(
      "Notification",
      MIXPANEL_SCREENS.NOTIFICATIONS,
      {
        notification_type: notification.type,
        seconds_since_created: secondsSinceCreated(),
        surface: MIXPANEL_NOTIFICATION_SURFACES.POPUP,
      },
    );
    handleMarkAsRead();
    window.open(notificationLink, "_blank");
  };

  const handleDismissClick = () => {
    mixpanelUtils.trackButtonClick(
      "Dismiss notification popup",
      MIXPANEL_SCREENS.NOTIFICATIONS,
      {
        notification_type: notification.type,
        seconds_since_created: secondsSinceCreated(),
        surface: MIXPANEL_NOTIFICATION_SURFACES.POPUP,
      },
    );
    onDismiss(notification.id);
  };

  /* Render */
  // The redeemer's pet celebrating with the same jump + sparkles the shop
  // plays on an unlock, wearing the incentive token.
  const renderIncentiveCelebration = () => {
    if (!incentive) return null;
    const redeemerPet = (pets ?? []).find(
      (pet) => pet.userId === incentive.redeemerUserId,
    );

    return (
      <Column
        key={celebrationSequence}
        className="relative shrink-0 items-center justify-center px-1"
      >
        <Column className="staffer-pet-shop-sparkle absolute -top-1 left-0 h-2.5 w-2.5 bg-amber-400 opacity-0" />
        <Column className="staffer-pet-shop-sparkle absolute right-0 top-3 h-3 w-3 bg-violet-500 opacity-0 [animation-delay:120ms]" />
        <Column className="staffer-pet-shop-sparkle absolute bottom-3 left-0 h-3 w-3 bg-orange-500 opacity-0 [animation-delay:240ms]" />
        <Column className="staffer-pet-shop-sparkle absolute -bottom-1 right-1 h-2.5 w-2.5 bg-emerald-400 opacity-0 [animation-delay:360ms]" />
        {redeemerPet ? (
          <Column className="staffer-pet-bounce-triple relative shrink-0">
            <StafferPetProfileSprite
              pet={redeemerPet}
              sizePx={SUBMITTER_PET_SIZE_PX}
            />
            <Row className="absolute bottom-0 right-0">
              <StafferPetIncentiveToken sizePx={20} />
            </Row>
          </Column>
        ) : (
          <StafferPetIncentiveToken
            className="staffer-pet-bounce-triple"
            sizePx={44}
          />
        )}
      </Column>
    );
  };

  const renderLeftRail = () => {
    if (incentive) return renderIncentiveCelebration();
    if (isTeamWideSubmit) return renderSubmitterPets();
    return renderActionRail();
  };

  // Show a staffer's pet and their coin growth from the submits
  const renderSubmitterPets = () => {
    const { submitters } =
      notification.payload as NotificationTeamWideSubmitPayload;
    const petByUserId = new Map((pets ?? []).map((pet) => [pet.userId, pet]));
    const petEntries = submitters
      .map((submitter) => {
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        const searchCount = submitter.searchCount || 0;
        return {
          pet: petByUserId.get(submitter.userId),
          coins:
            (submitter.count - searchCount) *
              STAFFER_PET_REWARD_DETAILS.SUBMIT.amount +
            searchCount * STAFFER_PET_SEARCH_SUBMIT_REWARD.amount,
        };
      })
      .filter(
        (entry): entry is { pet: Pet; coins: number } =>
          entry.pet !== undefined,
      )
      .slice(0, MAX_SUBMITTER_PETS);

    if (petEntries.length === 0) return null;

    return (
      <Row className="-ml-3 shrink-0 items-center">
        {renderPetColumn(petEntries.slice(0, SUBMITTER_PETS_PER_COLUMN))}
        {petEntries.length > SUBMITTER_PETS_PER_COLUMN
          ? renderPetColumn(
              petEntries.slice(SUBMITTER_PETS_PER_COLUMN),
              "-ml-4",
            )
          : null}
      </Row>
    );
  };

  const renderPetColumn = (
    petEntries: { pet: Pet; coins: number }[],
    className?: string,
  ) => (
    <Column className={cn("shrink-0 items-center justify-center", className)}>
      {petEntries.map((entry, index) => (
        <Column
          key={index}
          className={cn("relative shrink-0", index > 0 && "-mt-3")}
        >
          <StafferPetProfileSprite
            pet={entry.pet}
            sizePx={SUBMITTER_PET_SIZE_PX}
          />
          <Row className="absolute bottom-0 right-0 items-center gap-0.5 rounded-full border border-amber-200 bg-white px-1 shadow-sm">
            <CoinsIcon className="h-2.5 w-2.5 text-amber-500" />
            <Text className="font-mono text-[9px] font-black leading-none text-slate-900">
              {entry.coins}
            </Text>
          </Row>
        </Column>
      ))}
    </Column>
  );

  const renderActionRail = () => (
    <Column
      className="shrink-0 items-center justify-around"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <NotificationMarkReadButton
        isUnread
        notificationId={notification.id}
        notificationType={notification.type}
        trackingSurface={MIXPANEL_NOTIFICATION_SURFACES.POPUP}
      />
    </Column>
  );

  // Warns that leadership hears about this next hour, then reports it once they have
  const renderSimbaBadge = () => {
    if (isEscalated) {
      return (
        <Row className="shrink-0 items-center gap-1 rounded-full bg-red-50 px-2 py-0.5">
          <CircleAlertIcon className="h-3 w-3 shrink-0 text-red-600" />
          <Text className="text-xs font-semibold text-red-700">
            Simba notified
          </Text>
        </Row>
      );
    }

    if (!isEscalatingNextNudge) return null;

    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Row className="shrink-0 items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5">
              <TriangleAlertIcon className="h-3 w-3 shrink-0 text-amber-600" />
              <Text className="text-xs font-semibold text-amber-700">
                Simba
              </Text>
            </Row>
          </TooltipTrigger>
          <TooltipContent className="z-[70] max-w-xs">
            Simba gets messaged in an hour if this candidate is not acted on
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const render = () => (
    <Row
      className={cn(
        "pointer-events-auto w-full gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-lg transition-shadow hover:shadow-xl",
        CORNER_POPUP_MIN_HEIGHT_CLASS,
        "ease-out animate-in fade-in zoom-in-95 slide-in-from-bottom-6 fill-mode-both [animation-duration:450ms]",
        isExiting &&
          "pointer-events-none ease-in animate-out fade-out zoom-out-95 slide-out-to-right-8 fill-mode-forwards [animation-duration:300ms]",
        notificationLink && "cursor-pointer",
      )}
      onClick={handleCardClick}
    >
      {renderLeftRail()}

      <Column className="min-w-0 flex-1 justify-around">
        {/* Titles carry the company and job, so the truncated tail matters. */}
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              {/* Text forwards no ref, so the Row is the trigger. */}
              <Row className="min-w-0 items-center gap-1">
                <Text className="text-sm font-semibold text-slate-900" truncate>
                  {title}
                </Text>
                {/* Sibling rather than inline: the title truncates, and the
                    bot has to survive that. */}
                {isStevieCall ? (
                  <BotIcon className="h-4 w-4 shrink-0 text-primary-700" />
                ) : null}
                {/* Rides with the bot so the rating jump reads off the title. */}
                {isElevatedStevieCall ? (
                  <TrendingUpIcon className="h-4 w-4 shrink-0 text-emerald-600" />
                ) : null}
              </Row>
            </TooltipTrigger>
            <TooltipContent className="z-[70] max-w-xs">
              {title}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <Text className={cn("shrink-0 text-slate-500", contentSizeClassName())}>
          {content}
        </Text>
        <Row className="items-center justify-between gap-2">
          <Text className="text-xs text-slate-400">
            {dates.timeAgoForPipeline(notification.createdAt)}
          </Text>
          {renderSimbaBadge()}
          {onDismissAll ? (
            <Button
              // Reads as a peer of the timeago it sits beside, not an action.
              className="h-5 shrink-0 px-1.5 text-xs font-semibold text-slate-400 hover:text-slate-600"
              onClick={(e) => {
                e.stopPropagation();
                onDismissAll();
              }}
              size="sm"
              variant="ghost"
            >
              Dismiss all
            </Button>
          ) : null}
        </Row>
      </Column>

      <Button
        className="h-7 w-7 shrink-0"
        onClick={(e) => {
          e.stopPropagation();
          handleDismissClick();
        }}
        size="icon"
        variant="ghost"
      >
        <XIcon className="h-4 w-4 text-slate-500" />
      </Button>
    </Row>
  );

  return render();
};
